import { z } from 'zod';

import {
  cashForceCloseModeValues,
  cashOpeningModeValues,
  cashSessionCloseModeValues,
  cashSessionStatusValues,
  type CashForceCloseMode,
  type CashOpeningMode,
  type CashDenominationDto,
  type CashSessionDto,
  type CashSessionCloseMode,
  type CashSessionStatus,
  type CloseCashSessionRequest,
  type CloseCashSessionResponse,
  type ForceCloseCashSessionRequest,
  type ForceCloseCashSessionResponse,
  type GetCurrentCashSessionRequest,
  type GetCurrentCashSessionResponse,
  type ListCashSessionsRequest,
  type ListCashSessionsResponse,
  type OpenCashSessionRequest,
  type OpenCashSessionResponse,
} from './cash-sessions.dto.js';

const timestampSchema = z.iso.datetime();

export const CashSessionStatusSchema: z.ZodType<CashSessionStatus> =
  z.enum(cashSessionStatusValues);
export const CashOpeningModeSchema: z.ZodType<CashOpeningMode> = z.enum(cashOpeningModeValues);
export const CashSessionCloseModeSchema: z.ZodType<CashSessionCloseMode> = z.enum(
  cashSessionCloseModeValues,
);
export const CashForceCloseModeSchema: z.ZodType<CashForceCloseMode> =
  z.enum(cashForceCloseModeValues);
export const CashDenominationSchema: z.ZodType<CashDenominationDto> = z.object({
  denom: z.number().int().positive(),
  count: z.number().int().nonnegative(),
});

export const CashSessionDtoSchema: z.ZodType<CashSessionDto> = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  storeId: z.uuid(),
  locationId: z.uuid(),
  stationId: z.uuid(),
  cashLocationId: z.uuid(),
  status: CashSessionStatusSchema,
  openedAt: timestampSchema,
  closedAt: timestampSchema.nullable(),
  openedByUserId: z.uuid(),
  closedByUserId: z.uuid().nullable(),
  openingLedgerBalanceMinor: z.number().int().nullable(),
  openingMode: CashOpeningModeSchema,
  openingBasisAmountMinor: z.number().int(),
  openingCountedAmountMinor: z.number().int().nullable(),
  openingCountDenominations: z.array(CashDenominationSchema).nullable().optional(),
  currentExpectedCashMinor: z.number().int().nullable(),
  closeMode: CashSessionCloseModeSchema.nullable(),
  closeExpectedCashMinor: z.number().int().nullable(),
  closeCountedCashMinor: z.number().int().nullable(),
  closeVarianceAmountMinor: z.number().int().nullable(),
  openingNote: z.string().trim().min(1).max(500).nullable(),
  closingNote: z.string().trim().min(1).max(500).nullable(),
  closingDenominations: z.array(CashDenominationSchema).nullable().optional(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const OpenCashSessionRequestSchema: z.ZodType<OpenCashSessionRequest> = z
  .object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    occurredAt: timestampSchema.optional(),
    stationId: z.uuid(),
    storeId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    cashLocationId: z.uuid(),
    openingMode: CashOpeningModeSchema.optional(),
    openingCountedAmountMinor: z.number().int().nullable().optional(),
    openingCountDenominations: z.array(CashDenominationSchema).nullable().optional(),
    openingNote: z.string().trim().min(1).max(500).nullable().optional(),
  })
  .superRefine((value, context) => {
    const openingMode = value.openingMode ?? 'verified';
    if (openingMode === 'verified' && value.openingCountedAmountMinor == null) {
      context.addIssue({
        code: 'custom',
        message: 'Verified opening requires openingCountedAmountMinor.',
        path: ['openingCountedAmountMinor'],
      });
    }
  });

export const OpenCashSessionResponseSchema: z.ZodType<OpenCashSessionResponse> = z.object({
  cashSession: CashSessionDtoSchema,
});

export const CloseCashSessionRequestSchema: z.ZodType<CloseCashSessionRequest> = z.object({
  companyId: z.uuid(),
  operationId: z.uuid(),
  occurredAt: timestampSchema.optional(),
  cashSessionId: z.uuid(),
  stationId: z.uuid(),
  storeId: z.uuid().optional(),
  locationId: z.uuid().optional(),
  countedCashMinor: z.number().int(),
  closingNote: z.string().trim().min(1).max(500).nullable().optional(),
  denominations: z.array(CashDenominationSchema).nullable().optional(),
});

export const CloseCashSessionResponseSchema: z.ZodType<CloseCashSessionResponse> = z.object({
  cashSession: CashSessionDtoSchema,
});

export const ForceCloseCashSessionRequestSchema: z.ZodType<ForceCloseCashSessionRequest> = z
  .object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    occurredAt: timestampSchema.optional(),
    cashSessionId: z.uuid(),
    stationId: z.uuid(),
    storeId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    mode: CashForceCloseModeSchema,
    reasonNote: z.string().trim().min(1).max(500),
    countedCashMinor: z.number().int().nullable().optional(),
    denominations: z.array(CashDenominationSchema).nullable().optional(),
  })
  .superRefine((value, context) => {
    if (value.mode === 'counted' && value.countedCashMinor == null) {
      context.addIssue({
        code: 'custom',
        message: 'Counted force-close requires countedCashMinor.',
        path: ['countedCashMinor'],
      });
    }

    if (value.mode === 'unverified') {
      if (value.countedCashMinor != null) {
        context.addIssue({
          code: 'custom',
          message: 'Unverified force-close must not include countedCashMinor.',
          path: ['countedCashMinor'],
        });
      }
      if (value.denominations && value.denominations.length > 0) {
        context.addIssue({
          code: 'custom',
          message: 'Unverified force-close must not include denominations.',
          path: ['denominations'],
        });
      }
    }
  });

export const ForceCloseCashSessionResponseSchema: z.ZodType<ForceCloseCashSessionResponse> =
  z.object({
    cashSession: CashSessionDtoSchema,
  });

export const GetCurrentCashSessionRequestSchema: z.ZodType<GetCurrentCashSessionRequest> = z.object(
  {
    companyId: z.uuid(),
    stationId: z.uuid(),
    storeId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    cashLocationId: z.uuid(),
  },
);

export const GetCurrentCashSessionResponseSchema: z.ZodType<GetCurrentCashSessionResponse> =
  z.object({
    cashSession: CashSessionDtoSchema.nullable(),
  });

export const ListCashSessionsRequestSchema: z.ZodType<ListCashSessionsRequest> = z.object({
  companyId: z.uuid(),
  storeId: z.uuid().optional(),
  locationId: z.uuid().optional(),
  stationId: z.uuid().optional(),
  cashLocationId: z.uuid().optional(),
  status: CashSessionStatusSchema.optional(),
  from: timestampSchema.optional(),
  to: timestampSchema.optional(),
});

export const ListCashSessionsResponseSchema: z.ZodType<ListCashSessionsResponse> = z.object({
  items: z.array(CashSessionDtoSchema),
});
